import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import {
  removeWillChangeOnAnimationEnd,
  requestIdleCallback,
} from '@theme/utilities';

/**
 * Run after the next paint. Used so INP can complete on the first visual frame.
 * `scheduler.yield()` / a lone rAF can resume *before* paint and inflate presentation delay.
 * @param {() => void} callback
 */
function afterNextPaint(callback) {
  requestAnimationFrame(() => {
    setTimeout(callback, 0);
  });
}

/**
 * Wait for the drawer's transform slide without `getAnimations()` (avoids forced style).
 * @param {HTMLElement} element
 * @returns {Promise<void>}
 */
function waitForTransformTransition(element) {
  return new Promise((resolve) => {
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      element.removeEventListener('transitionend', onEnd);
      resolve();
    };

    /** @param {TransitionEvent} event */
    const onEnd = (event) => {
      if (event.target !== element || event.propertyName !== 'transform') return;
      finish();
    };

    element.addEventListener('transitionend', onEnd);
    // --drawer-animation-speed is 0.1s; buffer for slow devices / reduced-motion skips
    setTimeout(finish, 250);
  });
}

/**
 * A custom element that manages the main menu drawer.
 *
 * Click handlers only flip compositor-friendly classes so INP stays under 100ms.
 * `menu-open`, focus, nested resets, and `[open]` run after the next paint.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {HTMLElement | null} */
  #backdrop = null;

  /** @type {AbortController | null} */
  #openTask = null;

  /** @type {AbortController | null} */
  #closeTask = null;

  /** Invalidates in-flight close animation callbacks when re-opening. */
  #closeToken = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#backdrop = this.querySelector('.menu-drawer__backdrop');
    this.addEventListener('keyup', this.#onKeyUp);
    this.refs.details.addEventListener('toggle', this.#onDetailsToggle);
    this.refs.menuDrawer.addEventListener('animationend', this.#onAnimatedElementEnd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.refs.details.removeEventListener('toggle', this.#onDetailsToggle);
    this.refs.menuDrawer.removeEventListener('animationend', this.#onAnimatedElementEnd);
    this.#cancelDeferredWork();
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Keep presentation classes aligned when [open] changes (e.g. theme editor).
   */
  #onDetailsToggle = () => {
    const { details } = this.refs;
    if (details !== this.refs.details) return;

    if (details.open) {
      this.#presentDrawer();
    } else {
      this.#teardownDrawer();
    }
  };

  /**
   * @param {AnimationEvent} event
   */
  #onAnimatedElementEnd = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!target.classList.contains('menu-drawer__animated-element')) return;
    removeWillChangeOnAnimationEnd(event);
  };

  #presentDrawer() {
    this.refs.menuDrawer.classList.add('menu-drawer--rendered', 'menu-drawer--visible');
    this.#backdrop?.classList.add('menu-drawer__backdrop--visible');
  }

  /** Transform-only — first close paint stays on the compositor. */
  #unpresentPanel() {
    this.refs.menuDrawer.classList.remove('menu-drawer--visible');
  }

  #unpresentBackdrop() {
    this.#backdrop?.classList.remove('menu-drawer__backdrop--visible');
  }

  #teardownDrawer() {
    this.refs.menuDrawer.classList.remove('menu-drawer--visible', 'menu-drawer--rendered');
    this.#backdrop?.classList.remove('menu-drawer__backdrop--visible');
  }

  #cancelDeferredWork() {
    this.#openTask?.abort();
    this.#openTask = null;
    this.#closeTask?.abort();
    this.#closeTask = null;
  }

  /**
   * Whether the main drawer panel is presented (supports reopen mid-close).
   */
  get isOpen() {
    return this.refs.menuDrawer.classList.contains('menu-drawer--visible');
  }

  /**
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault();
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    const isMainDrawer = details === this.refs.details;

    if (isMainDrawer) {
      this.#closeToken += 1;
      this.#cancelDeferredWork();

      // Critical path: scoped presentation classes only (compositor-friendly).
      this.#presentDrawer();

      const token = this.#closeToken;
      afterNextPaint(() => {
        if (token !== this.#closeToken) return;
        if (!this.refs.menuDrawer.classList.contains('menu-drawer--visible')) return;

        details.open = true;
        this.#finalizeOpen(details, summary, target);
      });
      return;
    }

    this.#finalizeOpen(details, summary, target);
  }

  /**
   * @param {HTMLDetailsElement} details
   * @param {HTMLElement} summary
   * @param {string} [target]
   */
  #finalizeOpen(details, summary, target) {
    summary.setAttribute('aria-expanded', 'true');
    details.classList.add('menu-open');

    if (target) {
      this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
    }

    requestIdleCallback(() => {
      if (!details.open && !details.classList.contains('menu-open')) return;

      this.preventInitialAccordionAnimations(details);

      const drawer =
        details === this.refs.details
          ? this.refs.menuDrawer
          : details.querySelector('.menu-drawer, .menu-drawer__submenu');

      const el = drawer instanceof HTMLElement ? drawer : details;
      waitForTransformTransition(el).then(() => {
        if (details.open || details.classList.contains('menu-open')) {
          trapFocus(details);
        }
      });
    });
  }

  /**
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  close() {
    this.#close(this.refs.details);
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const isMain = details === this.refs.details;

    if (!isMain) {
      const summary = details.querySelector('summary');
      if (!summary) return;
      this.#finalizeClose(details, summary, false, this.#closeToken);
      return;
    }

    this.#openTask?.abort();
    this.#openTask = null;

    const token = ++this.#closeToken;

    // Critical path for <100ms INP: one transform class flip. Nothing else.
    this.#unpresentPanel();

    afterNextPaint(() => {
      if (token !== this.#closeToken) return;
      this.#runCloseAfterPaint(details, token);
    });
  }

  /**
   * Backdrop fade, menu-open teardown, and focus cleanup — only after first close paint.
   * @param {HTMLDetailsElement} details
   * @param {number} token
   */
  async #runCloseAfterPaint(details, token) {
    this.#closeTask?.abort();
    const abort = new AbortController();
    this.#closeTask = abort;

    // Fade overlay after the panel has already started sliding (keeps first paint cheap).
    this.#unpresentBackdrop();

    const animDone = waitForTransformTransition(this.refs.menuDrawer);

    // Yield once more before touching .menu-open (full drawer-tree style work).
    await new Promise((resolve) => setTimeout(resolve, 0));
    if (abort.signal.aborted || token !== this.#closeToken) return;

    const summary = details.querySelector('summary');
    summary?.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    await animDone;
    if (abort.signal.aborted || token !== this.#closeToken) return;

    reset(details);
    this.#teardownDrawer();
    removeTrapFocus();

    requestIdleCallback(() => {
      if (token !== this.#closeToken) return;
      const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
      openDetails.forEach(reset);
    });
  }

  /**
   * @param {HTMLDetailsElement} details
   * @param {HTMLElement} summary
   * @param {boolean} isMain
   * @param {number} token
   */
  #finalizeClose(details, summary, isMain, token) {
    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    if (isMain) {
      this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    }

    const drawer = isMain
      ? this.refs.menuDrawer
      : details.querySelector('.menu-drawer, .menu-drawer__submenu');

    const el = drawer instanceof HTMLElement ? drawer : details;

    waitForTransformTransition(el).then(() => {
      if (isMain && token !== this.#closeToken) return;

      reset(details);
      if (isMain) {
        this.#teardownDrawer();
        removeTrapFocus();
        requestIdleCallback(() => {
          if (token !== this.#closeToken) return;
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        });
      } else {
        trapFocus(this.refs.details);
      }
    });
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');
    if (!content.length) return;

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
